/**
 * Defensive readers for `ProgressUpload.ocr_metadata`.
 *
 * `ocr_metadata` is a JSONB passthrough, not a validated schema: the backend
 * writes whichever dict the branch it took happens to build, and no Pydantic
 * model fills the gaps on the way out. The judge-era degrade path
 * (`core/tasks.py::_finalize_pending_review`, PRD-43 §FR-6.1 / §FR-5.3) writes
 * only `{phase, reason, actor, questions, images, review_summary}` — every
 * scoring key is ABSENT, not null. Rows written that way already exist and
 * will never be backfilled.
 *
 * That distinction is the whole reason this file exists: `undefined === null`
 * is false, so every `x === null ? fallback : x.something()` guard in the
 * review surfaces sailed straight past the degraded rows and threw. These
 * helpers collapse "absent", "null" and "NaN" into one honest `null` so a
 * call site can have exactly one branch.
 *
 * They live in `lib/` because both `features/grading` (the legacy panel) and
 * `features/grading-review` (the queue) need them, and a cross-feature import
 * is banned by `.claude/rules/architecture.md`.
 */

import { GRADING_REVIEW_FLAGS, type GradingReviewFlag } from "@/constants/grading"
import type { GradingMetadata, GradingQuestion } from "@/types"

/** True for a real, usable number — rejects `undefined`, `null` and `NaN`. */
const isUsableNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value)

/**
 * The pipeline's suggested score, or `null` when it never produced one.
 *
 * Both review surfaces seed their score input from this, so it is the single
 * place that decides "no suggestion" — `String(undefined)` used to put the
 * literal text `undefined` in the field the professor types their grade into.
 */
export const gradingDraftScore = (metadata: GradingMetadata | null | undefined): number | null =>
  isUsableNumber(metadata?.draft_score) ? metadata.draft_score : null

const KNOWN_REVIEW_FLAGS: ReadonlySet<string> = new Set(Object.values(GRADING_REVIEW_FLAGS))

/**
 * PRD-67 — the flags this SPA knows how to explain, in the order written.
 *
 * `review_flags` is absent on every row written before PRD-67 and on the
 * degrade path; an unknown value (a newer backend) is dropped rather than
 * rendered as a raw key.
 */
export const gradingReviewFlags = (
  source: { review_flags?: readonly string[] | null } | null | undefined,
): GradingReviewFlag[] =>
  (source?.review_flags ?? []).filter((flag): flag is GradingReviewFlag =>
    KNOWN_REVIEW_FLAGS.has(flag),
  )

/**
 * PRD-67 — does this upload carry ANY review flag, known to this build or
 * not? This is what withholds the pre-fill; `gradingReviewFlags` is only for
 * rendering the ones the SPA can explain.
 */
export const gradingHasReviewFlags = (
  source: { review_flags?: readonly string[] | null } | null | undefined,
): boolean => (source?.review_flags ?? []).length > 0

/** Per-question rows, or `[]` on a degraded row that has none. */
export const gradingQuestions = (metadata: GradingMetadata | null | undefined): GradingQuestion[] =>
  metadata?.questions ?? []

/**
 * `correct / total`, or `null` when either half is missing.
 *
 * Returned as a pair rather than two readers so a call site cannot render
 * "3 de undefined": there is no way to have one without the other.
 */
export const gradingCorrectSummary = (
  metadata: GradingMetadata | null | undefined,
): { correct: number; total: number } | null =>
  isUsableNumber(metadata?.questions_correct) && isUsableNumber(metadata?.questions_total)
    ? { correct: metadata.questions_correct, total: metadata.questions_total }
    : null

/**
 * A 0–1 confidence as a whole percent, or `null` when there is no signal.
 *
 * `confidence` is typed `number` because the judge always writes one, but the
 * value crosses the wire as untyped JSON: a legacy row missing the key made
 * `Math.round(undefined * 100)` render "Confianza: NaN%" next to a perfectly
 * readable answer.
 */
export const gradingConfidencePercent = (confidence: number | null | undefined): number | null =>
  isUsableNumber(confidence) ? Math.round(confidence * 100) : null
